import {
    IErrorTrackingProps,
    trackError as trackErrorUtils
} from '~/services/logger/error.utils';
import {
    TContext,
    logDebug as logDebugUtil,
    logInfo as logInfoUtil,
    logWarn as logWarnUtil,
    logError as logErrorUtil
} from '~/services/logger/logger.utils';
import {
    IReportActionProps,
    reportAction
} from '~/services/logger/action.utils';

export interface ILoggerServiceProps {
    /* Will be added to the context. e.g *one_home*, *onboarding_quiz*.  */
    featureName?: string;
    /* Will be added to the context as the value of *featureName*. Defaults to *1*. */
    featureVersion?: number;
    /* Common context props which will always be added to the context. e.g. *{flow_id: flow_id}* */
    commonContext?: TContext;
    /* Provide a prefix for every message. e.g. *[OneHome]*, *[OnboardingQuiz]*. */
    messagePrefix?: string;
    /* The sample rate for the logger. Defaults to *1*. */
    sampleRate?: number;
}

/**
 * Aggregates common logging functionality and allows to provide common context props and a message prefix
 * (which will be passed to any of the logging methods).
 *
 * Use it as is, or inside your feature's context provider.
 *
 * @example
 * See OneHomeContext.provider.tsx
 */
export class LoggerService {
    protected featureName: string;
    protected featureVersion: number;
    protected commonContext: TContext;
    protected messagePrefix: string;
    protected sampleRate: number;

    constructor({
        featureName,
        featureVersion = 1,
        commonContext = {},
        messagePrefix,
        sampleRate = 1
    }: ILoggerServiceProps) {
        this.featureName = featureName;
        this.featureVersion = featureVersion;
        this.commonContext = commonContext;
        this.messagePrefix = messagePrefix;
        this.sampleRate = sampleRate;
    }

    private getContext(originalContext: TContext = {}): TContext {
        const context: TContext = { ...this.commonContext, ...originalContext };

        if (this.featureName) {
            context[this.featureName] = this.featureVersion;
        }

        return Object.keys(context).length === 0 ? undefined : context;
    }

    private getMessage(originalMessage: string) {
        if (!originalMessage) {
            return;
        }

        if (this.messagePrefix) {
            return `${this.messagePrefix} ${originalMessage}`;
        }

        return originalMessage;
    }

    /**
     * Get a new LoggerUtils (for a sub feature) which extends the current LoggerUtils
     */
    extend({
        featureName,
        featureVersion = 1,
        commonContext = {},
        messagePrefix
    }: ILoggerServiceProps) {
        return new LoggerService({
            featureName: featureName,
            featureVersion: featureVersion,
            commonContext: { ...this.getContext(commonContext) },
            messagePrefix: this.messagePrefix
                ? `${this.messagePrefix} ${messagePrefix}`
                : messagePrefix
        });
    }

    /**
     * Tracks an Error (to DD RUM)
     */
    trackError(errorProps: Omit<IErrorTrackingProps, 'logger'>) {
        const message = this.getMessage(errorProps.message);

        trackErrorUtils({
            error: errorProps.error,
            ...(message && { message }),
            context: this.getContext(errorProps.context)
        });
    }

    /**
     * Logs a debug message (to DD Logs)
     */
    logDebug(message: string, context: TContext = {}) {
        logDebugUtil(this.getMessage(message), this.getContext(context));
    }

    /**
     * Logs an info message (to DD Logs)
     */
    logInfo(message: string, context: TContext = {}, sampleRate?: number) {
        logInfoUtil(
            this.getMessage(message),
            this.getContext(context),
            sampleRate || this.sampleRate
        );
    }

    /**
     * Logs a warn message (to DD Logs)
     */
    logWarn(message: string, context: TContext = {}, sampleRate?: number) {
        logWarnUtil(
            this.getMessage(message),
            this.getContext(context),
            sampleRate || this.sampleRate
        );
    }

    /**
     * Logs an error message (to DD Logs)
     */
    logError(message: string, context: TContext = {}, sampleRate?: number) {
        logErrorUtil(
            this.getMessage(message),
            this.getContext(context),
            sampleRate || this.sampleRate
        );
    }

    trackUserAction(trackActionProps: IReportActionProps) {
        const { name, context = {} } = trackActionProps;

        reportAction({
            name: name, // actionName should not be prefixed with messagePrefix
            context: this.getContext(context)
        });
    }
}
